import { useState, useEffect } from 'react';
import { hrAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

const FIELDS = [
  { key: 'phone', label: 'Phone Number', placeholder: '(555) 123-4567' },
  { key: 'address', label: 'Home Address', placeholder: '123 Main St, City, Province' },
  { key: 'date_of_birth', label: 'Date of Birth', type: 'date' },
  { key: 'emergency_contact_name', label: 'Emergency Contact Name' },
  { key: 'emergency_contact_phone', label: 'Emergency Contact Phone' },
  { key: 'emergency_contact_relation', label: 'Emergency Contact Relationship' },
];

export const MyInfoTab = () => {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    hrAPI.getProfile().then((res) => {
      setProfile(res.data);
      setForm(res.data.hr_profile || {});
    }).catch(() => toast.error('Failed to load profile'));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await hrAPI.updateProfile(form);
      setForm(res.data.hr_profile || {});
      toast.success('Personal information updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="hr-myinfo-tab">
      <Card className="bg-white border-slate-200">
        <CardHeader><CardTitle className="text-slate-800 text-lg">Employment Details</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div><p className="text-slate-400 text-xs">Full Name</p><p className="text-slate-800 font-medium" data-testid="hr-profile-name">{profile.name}</p></div>
          <div><p className="text-slate-400 text-xs">Work Email</p><p className="text-slate-800">{profile.email || '—'}</p></div>
          <div><p className="text-slate-400 text-xs">Role</p><p className="text-slate-800 capitalize">{profile.role}</p></div>
          <div><p className="text-slate-400 text-xs">Member Since</p><p className="text-slate-800">{profile.created_at ? new Date(profile.created_at).toLocaleDateString() : '—'}</p></div>
          <p className="text-xs text-slate-400 pt-2">Name, email and role changes must be made by an administrator.</p>
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200 lg:col-span-2">
        <CardHeader><CardTitle className="text-slate-800 text-lg">Personal Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <Label className="text-slate-700">{f.label}</Label>
                <Input
                  type={f.type || 'text'}
                  value={form[f.key] || ''}
                  placeholder={f.placeholder}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="bg-white border-slate-200 mt-1"
                  data-testid={`hr-profile-${f.key}-input`}
                />
              </div>
            ))}
          </div>
          <Button onClick={save} disabled={saving} className="bg-cyan-500 hover:bg-cyan-600 rounded-full mt-6" data-testid="hr-profile-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
